import { useCallback, useMemo, useState } from 'react';
import { useConsumptions } from '../../../hooks/useConsumptions';
import { ITEMS_PER_PAGE } from '../constants/consumptionsConstants';
import {
  mapConsumptionToDisplay,
} from '../utils/consumptionsUtils';

export const useConsumptionsTable = () => {
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedConsumptionId, setSelectedConsumptionId] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: consumptionsResponse,
    isLoading,
    refetch,
  } = useConsumptions({
    page,
    per_page: rowsPerPage,
    search: searchTerm,
  });

  const consumptions = useMemo(() => {
    if (Array.isArray(consumptionsResponse)) return consumptionsResponse;
    return consumptionsResponse?.results || [];
  }, [consumptionsResponse]);

  const displayConsumptions = useMemo(
    () => consumptions.map((consumption) => mapConsumptionToDisplay(consumption)),
    [consumptions]
  );

  const data = displayConsumptions;
  const totalCount = Array.isArray(consumptionsResponse)
    ? data.length
    : consumptionsResponse?.total || 0;
  const totalPages = Math.max(
    1,
    Array.isArray(consumptionsResponse)
      ? Math.ceil(totalCount / rowsPerPage)
      : consumptionsResponse?.pages || 1
  );

  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((_, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  }, []);

  const handleViewDetail = (consumption) => {
    setSelectedConsumptionId(consumption.uuid);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedConsumptionId(null);
  };

  const handleRefreshList = async () => {
    await refetch();
  };

  return {
    data,
    isLoading,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    detailDialogOpen,
    selectedConsumptionId,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleViewDetail,
    handleCloseDetailDialog,
    handleRefreshList,
  };
};
